import { useTranslation } from '@pancakeswap/localization'
import { WalletConnectorNotFoundError, WalletSwitchChainError } from '@pancakeswap/ui-wallets'
import { ApolloWalletNotFoundError } from '@pancakeswap/wagmi/connectors/apollo'
import replaceBrowserHistory from '@pancakeswap/utils/replaceBrowserHistory'
import { CHAIN_QUERY_NAME } from 'config/chains'
import { ConnectorNames } from 'config/wallet'
import { useCallback } from 'react'
import { useAppDispatch } from 'state'
import { ConnectorNotFoundError, SwitchChainNotSupportedError, useAccount, useConnect, useDisconnect, useSwitchChain } from 'wagmi'
import { clearUserStates } from '../utils/clearUserStates'
import { useActiveChainId } from './useActiveChainId'
import { useSessionChainId } from './useSessionChainId'

const useAuth = () => {
  const dispatch = useAppDispatch()
  const { connectAsync, connectors } = useConnect()
  const { switchChainAsync } = useSwitchChain()
  const { chain } = useAccount()
  const { disconnectAsync } = useDisconnect()
  const { chainId } = useActiveChainId()
  const [, setSessionChainId] = useSessionChainId()
  const { t } = useTranslation()

  const login = useCallback(
    async (connectorID: ConnectorNames) => {
      const findConnector = connectors.find((c) => c.id === connectorID)
      try {
        if (!findConnector) return undefined

        const connected = await connectAsync({ connector: findConnector, chainId })

        if (connectorID === ConnectorNames.Apollo && connected.chainId !== chainId) {
          try {
            await switchChainAsync({ chainId, connector: findConnector })
            return { ...connected, chainId }
          } catch {
            if (CHAIN_QUERY_NAME[connected.chainId]) {
              replaceBrowserHistory('chain', CHAIN_QUERY_NAME[connected.chainId])
              setSessionChainId(connected.chainId)
            }
          }
        } else if (connected.chainId !== chainId) {
          replaceBrowserHistory('chain', CHAIN_QUERY_NAME[connected.chainId])
          setSessionChainId(connected.chainId)
        }
        return connected
      } catch (error) {
        if (error instanceof ApolloWalletNotFoundError) {
          throw new WalletSwitchChainError(
            t('Apollo Wallet not found. Please install the extension and refresh the page.'),
          )
        }
        if (error instanceof ConnectorNotFoundError) {
          throw new WalletConnectorNotFoundError()
        }
        if (
          error instanceof SwitchChainNotSupportedError
          // TODO: wagmi
          // || error instanceof SwitchChainError
        ) {
          throw new WalletSwitchChainError(t('Unable to switch network. Please try it on your wallet'))
        }
        throw error
      }
      return undefined
    },
    [connectors, connectAsync, switchChainAsync, chainId, setSessionChainId, t],
  )

  const logout = useCallback(async () => {
    try {
      await disconnectAsync()
    } catch (error) {
      console.error(error)
    } finally {
      clearUserStates(dispatch, { chainId: chain?.id })
    }
  }, [disconnectAsync, dispatch, chain?.id])

  return { login, logout }
}

export default useAuth
